<template>
	<div class="task-log-info-step" no-gutters>
		<div class="header-section flex-space-between--start">
			<div class="flex-space-between--start">
				<div class="flex-space-between--start">
					<SIcon class="mr-2" :name="stepIconClass" />
					<IntegrationIcon class="ml-2 mr-2" :integration-name="nodeData.serviceSlug" :size="25" />
				</div>
				<div class="title-info">
					<span class="header-title">
						Step {{ nodeData.sequence }}: <span class="subtle-message">{{ nodeData.actionTitle || 'Missing Action Title' }}</span>
					</span>
					<div class="subtle-message">{{ nodeData.created | formatDate }}</div>
					<div v-if="retryMessage" class="retry-count">{{ retryMessage }}</div>
				</div>
			</div>
			<div class="text-right display-flex flex-align-center">
				<!-- <div v-if="editStepUrl">
					<a class="primary-link-style" :href="editStepUrl">Edit step</a>
				</div> -->
				<CriticalErrorIndicator class="mr-4" v-if="isCriticalError" />
				<span class="node-run-id">{{ nodeData.id }}</span>
			</div>
		</div>
		<div v-if="nodeRunData !== null">
			<AppTabSelector :tabItems="tabItems" v-model="stepTabs" />
			<div class="tab-content mt-2">
				<SearchField :searchString.sync="searchString" />

				<div class="step-result-list scroll-on-overflow pa-3 pt-0">
					<div class="item-box" v-for="(value, name) in filterItems" :key="name + '_' + value">
						<DataTableRepresentation
							v-if="isTable(value)"
							:listKey="name"
							:input="value"
							:nodeUUID="nodeData.id"
							:tableItemOutputLabels="getTableOutputLabels(name)"
						/>
						<div v-else>
							<span class="var-name">{{ getReadableKey(name) }}</span>
							<span class="var-value pl-2">{{ value }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<p v-else class="ma-10 subtle-message">Input and output are unavailable.</p>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

import { getStepIconClass, getStatusText, STATUSES } from './TaskLogService';
import CriticalErrorIndicator from './common/CriticalErrorIndicator';
import SearchField from './common/SearchField';
import WidgetFormService from '../WidgetForm/WidgetFormService';
import DataTableRepresentation from '@/components/DataTableRepresentation/DataTableRepresentation';
import { selectedNodeRunData } from '@/store/modules/analytics/getters';
import { shouldFormatPropertyKey } from '@/store/modules/auth/getters';
import { editNodeLink } from '@/store/modules/nodes/getters';
import { getWorkflow, allWorkflows } from '@/store/modules/workflows/getters';
import ParamsService from '@/utils/ParamsService';
import { flattenPreserveArrays, isArrayOfObjects } from '@/utils/helpers';
import { pluralize } from '@/utils/language-service';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'TaskLogInfoStep',
	components: {
		DataTableRepresentation,
		SearchField,
		CriticalErrorIndicator,
	},
	props: {
		nodeData: { type: Object },
		flowData: { type: Object },
	},
	methods: {
		isTable(value) {
			return isArrayOfObjects(value);
		},
		getReadableKey(name) {
			return ParamsService.formatPropertyKeyToReadable(name, this[shouldFormatPropertyKey.fullPath]);
		},
		getTableOutputLabels(tableKey) {
			return this.outputLabels[tableKey];
		},
	},
	computed: {
		...mapGetters([shouldFormatPropertyKey.fullPath]),
		...mapGetters([selectedNodeRunData.fullPath, editNodeLink.fullPath, getWorkflow.fullPath, allWorkflows.fullPath]),
		filterItems() {
			if (this.searchString && this.dictArr) {
				return Object.fromEntries(
					Object.entries(this.dictArr).filter(([k, v]) => {
						if (this.isTable(v)) {
							const tableValues = WorkflowNodeService.replaceTableValueWithOutputLabels(k, v, this.outputLabels);
							v = JSON.stringify(tableValues);
							return String(v).toLowerCase().includes(this.searchString.toLowerCase());
						}
						return (
							String(v).toLowerCase().includes(this.searchString.toLowerCase()) ||
							String(k).toLowerCase().includes(this.searchString.toLowerCase())
						);
					})
				);
			} else {
				return this.dictArr;
			}
		},
		dictArr() {
			if (this.stepTabs == 1) {
				return this.processedOutput;
			}
			return this.processedInput;
		},
		step() {
			var o = {
				trigger_date: '15:23:12 18/12/21',
			};
			return o;
		},
		stepIconClass() {
			return getStepIconClass(this.nodeData.status);
		},
		nodeRunData() {
			return this[selectedNodeRunData.fullPath](this.nodeData.id);
		},
		nodeRunDataOutput() {
			return WorkflowNodeService.replaceKeysWithOutputLabels(this.nodeRunData.output, this.outputLabels);
		},
		processedOutput() {
			if (!this.nodeRunData) {
				return;
			}
			return flattenPreserveArrays(this.nodeRunDataOutput);
		},
		processedInput() {
			if (!this.nodeRunData) {
				return;
			}
			const input = {};
			for (const [key, value] of Object.entries(this.nodeRunData.input)) {
				try {
					input[key] = WidgetFormService.processPreviousSteps(value, this.nodeRunData.previousSteps); //todo add replace map here
				} catch (e) {
					console.log(e);
					input[key] = value;
				}
			}
			return flattenPreserveArrays(input);
		},
		outputLabels() {
			return this.nodeRunData.outputLabels || {};
		},
		// editStepUrl() {
		// todo - return this when we have a way of going to the previous node/flow that actually ran
		// and not to the current draft that may be totally different, and not correct according to the log
		// TODO: yes we can
		// 	if (this.flowData?.projectId && this.flowData.flowId && this.nodeData.nodeId) {
		// 		return this[editNodeLink.fullPath]({ projectId: this.flowData.projectId, flowId: this.flowData.flowId, nodeId: this.nodeData.nodeId });
		// 	}
		// 	return undefined;
		// },
		retryMessage() {
			if (getStatusText(this.nodeData.status) === STATUSES.SUCCESS_STATUS && this.nodeData.retryCount) {
				const retryCount = Number(this.nodeData.retryCount);
				if (retryCount > 0) {
					return `Succeeded after ${pluralize('retry', retryCount, true)}`;
				}
			}
			return '';
		},
		isCriticalError() {
			return Boolean(this.nodeData.criticalError);
		},
	},
	data: () => ({
		searchString: '',
		stepTabs: 0,
		tabItems: [
			{ title: 'Input', tab: 1 },
			{ title: 'Output', tab: 2 },
		],
	}),
};
</script>

<style scoped lang="scss">
.task-log-info-step {
	.header-section {
		font-size: 12px;

		.title-info {
			margin-top: -3px;

			.header-title {
				font-size: 14px;
			}
			.retry-count {
				color: #faaa5a;
			}
		}
	}

	.node-run-id {
		color: #bcbcbc;
		font-size: 10px;
	}

	.tab-content {
		border: var(--seconday-border);
		border-radius: 6px;

		.step-result-list {
			width: 100%;
			height: 265px;
			font-size: 13px;

			.item-box {
				max-width: 100%;
				overflow: hidden;
				padding: 2px 4px;

				.var-name {
					font-weight: 300;
				}
				.var-value {
					max-width: 100%;
					overflow: hidden;
					opacity: 0.4;
				}
			}
		}
	}
}
</style>
